import { Injector } from '@angular/core';
import { BehaviorSubject ,  Observable } from 'rxjs';
import { config } from '../../../config/config';
import { cgpCookieHelper } from '../../../modules/cgp-cookie-helper';
import { CgpAlertDialogService } from '../cgp-alert-dialog/cgp-alert-dialog.service';
import { cgpAuthentication } from '../../../modules/cgp-authentication';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

const applicationJson = 'application/json';

export abstract class BaseHttpService extends BehaviorSubject<any> {
    private readonly http: HttpClient;
    private readonly router: Router;
    private readonly cgpAlertDialogService: CgpAlertDialogService;
    loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    filteredData: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    httpErrorHeader: string;

    constructor(
        private readonly injector: Injector,
    ) {
        super(null);
        this.http = injector.get(HttpClient);
        this.router = injector.get(Router);
        this.cgpAlertDialogService = injector.get(CgpAlertDialogService);

        this.subscribe(x => {
            (x && x.length > 0) ? this.filteredData.next(x) : this.filteredData.next([x]);
            this.loading.next(false);
        });
    }


    /**
     * 
     * @param value 
     * @param field 
     */
    handleFilter = (value: string, field: string): void => {
        const check: string = value.toString();
        if (value) {
            const result = this.value
                .filter(x => {
                    try {
                        const source: string = x[field].toString().toLowerCase();
                        return source.indexOf(check.toLowerCase()) !== -1;
                    } catch (e) {
                        this.filteredData.next(this.value);
                    }
                });
            this.filteredData.next(result);
        }
        else {
            this.filteredData.next(this.value);
        }
    }


    /**
     * 
     */
    handleError = (error: HttpErrorResponse) => {
        this.loading.next(false);
        if (error.status >= 200 && error.status < 400) return;
        const isUnauthorized: boolean = error.status === 401;
        const _unauthMsg = ". Click OK to refresh token";
        const _body = this.getErrorBody(error) + (isUnauthorized ? _unauthMsg : "");
        const _status = `${error.status} ${error.statusText}`;
        if (this.cgpAlertDialogService.isOpen()) return;
        this.cgpAlertDialogService.showAlertDialog({
            title: this.httpErrorHeader ? this.httpErrorHeader : 'HTTP Error: ' + _status,
            message: _body,
            alert: 'danger',
            closeLabel: 'OK',
            onClosed: () => isUnauthorized ? this.refreshToken() : null,
        });
    }


    /**
     * 
     * @param url 
     * @param catchError 
     */
    protected fetch(url, catchError = true): Promise<any> {
        return this.handleResponse(this.http.get(url, this.getOptions()), catchError, true);
    }


    /**
     * 
     * @param body 
     * @param url 
     * @param catchError 
     */
    protected post(body: any, url: string, catchError = true, searchParams: HttpParams = null): Promise<any> {
        return this.handleResponse(this.http.post(url, JSON.stringify(body), { headers: this.getHeaders(), params: searchParams, observe: "response" }), catchError);
    }

    protected post_excel(body: any, url: string, catchError = true, searchParams: HttpParams = null): Promise<any> {
        return this.handleResponse(this.http.post(url, body, { headers: this.getExcelHeaders(), params: searchParams, observe: "response" }), catchError);
    }

    /**
     * 
     * @param body 
     * @param url 
     * @param catchError 
     */
    protected put(body: any, url: string, catchError = true): Promise<any> {
        return this.handleResponse(this.http.put(url, JSON.stringify(body), this.getOptions()), catchError);
    }


    /**
     * 
     * @param url 
     * @param catchError 
     */
    protected delete(url: string, catchError = true): Promise<any> {
        return this.handleResponse(this.http.delete(url, this.getOptions()), catchError);
    }


    /**
     * 
     * @param url 
     */
    protected download(body: any, url: string, searchParams: HttpParams = null, catchError = true): Promise<any> {
        const headers = this.getHeaders();
        const options = {
            headers,
            params: searchParams,
            observe: "response",
            responseType: "arraybuffer"
        } as any;

        return this.handleResponse(this.http.post(url, JSON.stringify(body), options), catchError);
    }


    /**
     * 
     * @param data 
     * @param url 
     */
    protected uploadS3Object(data: any, url: string): Promise<any> {
        return this.http.post(url, data, { headers: this.getS3Headers() })
            .toPromise()
            .catch(this.handleError);
    }


    /**
     * 
     * @param data 
     * @param url 
     */
    protected copyS3Object(data: any, url: string): Promise<any> {
        const _headers: HttpHeaders = this.getS3Headers(true)
            .set('Content-Type', `${applicationJson}; charset=utf-8`)
        return this.http.post(url, data, { headers: _headers })
            .toPromise()
            .catch(this.handleError);
    }


    /**
     * 
     * @param url 
     */
    protected removeS3Object(url: string): Promise<any> {
        return this.http.delete(url, { headers: this.getS3Headers() })
            .toPromise()
    }


    /**
     * 
     * @param url 
     */
    protected getS3Object(url: string): Promise<any> {
        return this.http.get(url, { headers: this.getS3Headers(), responseType: "blob" })
            .toPromise()
    }


    /**
      * 
      */
    private getS3Headers(isJson = false): HttpHeaders {
        const token: string = cgpCookieHelper
            .retrieveCookie(config.authentication.authenticationCookie)
            .replace('Bearer_', 'Bearer ');
        const _accept = isJson ? applicationJson : 'application/octet-stream';
        return new HttpHeaders()
            .set('Accept', _accept)
            .set('Authorization', token)
            .set('appId', 'mesc2')
    }


    /**
     * 
     */
    private getHeaders() {
        try {
            this.loading.next(true);
            const token: string = this.getToken();

            return new HttpHeaders({ 'Content-Type': applicationJson })
                .set('Accept', applicationJson)
                .set('Authorization', token);
        } catch (e) { }
    }


    /**
     * 
     */
    private getExcelHeaders() {
        try {
            this.loading.next(true);
            const token: string = this.getToken();

            return new HttpHeaders()
                .set('Accept', applicationJson)
                .set('Authorization', token);
        } catch (e) { }
    }


    /**
     * 
     */
    private getOptions(): any {
        return { headers: this.getHeaders(), observe: "response" } as any;
    }


    /**
     * 
     */
    private getToken(): string {
        const _token = cgpCookieHelper.retrieveCookie(config.authentication.authenticationCookie)
        return _token.replace('Bearer_', 'Bearer ');
    }


    /**
     * 
     * @param headers 
     */
    private updateToken(headers: any) {
        try {
            let newToken = headers.get(config.authentication.authenticationCookie);

            newToken = newToken.replace('Bearer ', '');
            cgpCookieHelper.setAuthCookie(newToken);
        } catch (e) { }
    }


    /**
     * 
     */
    private readonly refreshToken = () => {
        this.router.navigate(['/'])
        cgpAuthentication.refreshToken(null)
        window.location.reload();
    }


    /**
     * 
     * @param response 
     * @param catchError 
     */
    private handleResponse(response: Observable<any>, catchError = true, hasBody = false): Promise<any> {
        const responsePromise: Promise<any> = response
            .toPromise()
            .then(x => {
                this.updateToken(x.headers);
                if (x.body) {
                    this.next(x.body);
                    return x.body;
                }
                return x;
            });
        return catchError ? responsePromise
            .catch(e => this.handleError(e)) : responsePromise;
    }


    /**
     * 
     * @param error 
     */
    private getErrorBody(error: any): string {
        const _err = error.error ? error.error : error;
        if (typeof (_err) === typeof ({})) {
            const errors: any[] = [];
            for (const it in _err) {
                const e = _err[it].Errors
                if (e && e[0]) {
                    errors.push(e[0].ErrorMessage);
                }
            }
            return errors.join('\n');
        }
        return _err;
    }

}
